<script setup lang="ts">
import Modal from '@/Components/Modal.vue';
import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useCopy, renderCopy, consultantTermVars, blockVisible } from '@/copy';

const model = defineModel({
    type: Boolean,
    default: false,
})

const { part = 1, slug = '', title = '', hasChat = true, chatType = 'human' } = defineProps<{
    // Which participant instructions to show (1 or 2). Ignored when `slug` is given.
    part?: number
    // Explicit copy slug, for non-participant instructions (e.g. the consultant page).
    slug?: string
    title?: string
    // Part mode only: whether the session has an assistant; blocks whose `audience` excludes this
    // treatment are dropped, so a participant with no consultant never reads about one (and
    // no-help-only blocks stay hidden when there is one). Irrelevant in slug mode.
    hasChat?: boolean
    // Part mode only: consultant treatment ('human' | 'llm'), resolving the `consulente` merge
    // tag to the matching assistant term. Defaults to human when absent.
    chatType?: string | null
}>()

const copy = useCopy()
const page = usePage()

type OnboardingBlock = { instructions: string; audience?: string }

// The recap is now derived from the part's onboarding blocks (same source as the gated flow),
// so it can never drift from what the participant was onboarded with.
const partBlocks = computed<OnboardingBlock[]>(() => {
    const config = page.props.experimentConfigData as Record<string, any> | undefined
    const key = part === 2 ? 'onboardingPart2' : 'onboardingPart1'
    return (config?.[key] ?? []) as OnboardingBlock[]
})

const heading = computed(() => title || `Istruzioni Parte ${part}`)

const html = computed(() => {
    // Slug mode: a single explicit copy block (e.g. the consultant instructions).
    if (slug) {
        return renderCopy(copy(slug))
    }

    // Part mode: concatenate the part's blocks, dropping blocks whose `audience` excludes this
    // treatment and resolving the `consulente` merge tag to the treatment's assistant term.
    const vars = consultantTermVars(chatType)
    return partBlocks.value
        .filter((block) => blockVisible(block, hasChat))
        .map((block) => renderCopy((block.instructions ?? '').trim(), vars))
        .filter((text) => text.length > 0)
        .join('')
})
</script>

<template>
    <Modal v-model="model" :close-outside="true" :title="heading" :icon="false"
        action-label="Ho capito" width="w-240">
        <div class="instructions-body copy-html" v-html="html"></div>
    </Modal>
</template>

<style scoped>
@reference '#app.css';

/* The rendered HTML is styled by the global `copy-html` class (see app.css); a long document
   just needs looser lines than the modal default. */
.instructions-body {
    line-height: 1.5;
}
</style>
